import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useSite } from "../../siteContext";

const AnkPage = () => {
    const [goldenAnk, setGoldenAnk] = useState('');
    const [finalAnk, setFinalAnk] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ success: false, message: '' });
    const { site } = useSite();

    useEffect(() => {
        fetchAnkData();
    }, [site]);

    const fetchAnkData = async () => {
        try {
            const configDocRef = doc(db, 'config', site === "android" ? "config" : site);
            const configDocSnapshot = await getDoc(configDocRef);

            if (configDocSnapshot.exists()) {
                const configData = configDocSnapshot.data();
                // Convert array to comma-separated string for display
                setGoldenAnk(configData.golden_ank ? configData.golden_ank : '');
                setFinalAnk(configData.final_ank ? configData.final_ank.join(',') : '');
            }
        } catch (error) {
            console.error('Error fetching ank data:', error);
            setSaveStatus({
                success: false,
                message: 'Error loading data: ' + error.message
            });
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        setSaveStatus({ success: false, message: '' });
        
        try {
            const configDocRef = doc(db, 'config', site === "android" ? "config" : site);
            
            // Convert comma-separated string to array for final_ank
            const finalAnkArray = finalAnk.split(',').map(item => item.trim()).filter(item => item !== '');
            
            await updateDoc(configDocRef, {
                'golden_ank': goldenAnk,
                'final_ank': finalAnkArray
            });
            
            setSaveStatus({
                success: true,
                message: 'Ank data saved successfully!'
            });
            
        } catch (error) {
            console.error('Error saving ank data:', error);
            setSaveStatus({
                success: false,
                message: 'Error saving data: ' + error.message
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        setSaveStatus({ success: false, message: '' });
        
        try {
            const configDocRef = doc(db, 'config', site === "android" ? "config" : site);
            
            await updateDoc(configDocRef, {
                'golden_ank': '',
                'final_ank': []
            });
            
            setGoldenAnk('');
            setFinalAnk('');
            
            setSaveStatus({
                success: true,
                message: 'Ank data deleted successfully!'
            });
            
        } catch (error) {
            console.error('Error deleting ank data:', error);
            setSaveStatus({
                success: false,
                message: 'Error deleting data: ' + error.message
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center mx-auto mt-16 p-8">
            <h1 className="text-4xl font-bold mb-8 text-white">ANK Management</h1>
            
            <div className="w-full max-w-3xl bg-white shadow-md rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold mb-6 text-black">Update ANK Data</h3>
                <div className="space-y-6">
                    <div>
                        <label htmlFor="goldenAnk" className="text-xl font-semibold mb-2 block text-black">
                            Golden ANK
                        </label>
                        <input
                            id="goldenAnk"
                            type="text"
                            className="text-xl p-3 border border-gray-800 rounded-md w-full text-black"
                            value={goldenAnk}
                            onChange={(e) => setGoldenAnk(e.target.value)}
                            placeholder="Enter Golden ANK"
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="finalAnk" className="text-xl font-semibold mb-2 block text-black">
                            Final ANK (Comma Separated)
                        </label>
                        <textarea
                            id="finalAnk"
                            className="text-xl p-3 border border-gray-800 rounded-md w-full text-black"
                            value={finalAnk}
                            onChange={(e) => setFinalAnk(e.target.value)}
                            placeholder="Enter Final ANK values separated by commas (e.g. 1,2,3)"
                            rows={3}
                        />
                        <p className="text-sm text-gray-600 mt-1">Enter multiple values separated by commas (e.g. 1,2,3)</p>
                    </div>
                    
                    <div className="flex space-x-4">
                        <button
                            onClick={handleSave}
                            disabled={isLoading}
                            className={`mt-4 px-6 py-3 text-lg font-semibold text-white rounded-md transition duration-300 ease-in-out
                                ${isLoading 
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-500 hover:bg-blue-600'
                                }`}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                    </svg>
                                    Saving...
                                </span>
                            ) : 'Save ANK Data'}
                        </button>
                        
                        <button
                            onClick={handleDelete}
                            disabled={isLoading || (!goldenAnk && !finalAnk)}
                            className={`mt-4 px-6 py-3 text-lg font-semibold text-white rounded-md transition duration-300 ease-in-out
                                ${isLoading || (!goldenAnk && !finalAnk)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-500 hover:bg-red-600'
                                }`}
                        >
                            {isLoading ? 'Deleting...' : 'Delete ANK Data'}
                        </button>
                    </div>
                    
                    {saveStatus.message && (
                        <div className={`mt-4 p-3 rounded-md ${saveStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {saveStatus.message}
                        </div>
                    )}
                </div>
            </div>
            
            {/* View Saved ANK Data Section */}
            <div className="w-full max-w-3xl bg-white shadow-md rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-6 text-black">Current ANK Data</h3>
                
                <div className="space-y-6">
                    <div className="p-4 bg-gray-50 rounded-md border border-gray-300">
                        <h4 className="text-lg font-medium text-black mb-2">Golden ANK</h4>
                        {goldenAnk ? (
                            <p className="text-xl text-blue-600 font-bold">{goldenAnk}</p>
                        ) : (
                            <p className="text-gray-500 italic">No golden ANK set</p>
                        )}
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-md border border-gray-300">
                        <h4 className="text-lg font-medium text-black mb-2">Final ANK</h4>
                        {finalAnk ? (
                            <div className="flex flex-wrap gap-2">
                                {finalAnk.split(',').map((ank, index) => (
                                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-lg">
                                        {ank.trim()}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No final ANK values set</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AnkPage;

